import { DOCUMENT } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Injector, makeStateKey, OnDestroy, OnInit, TransferState } from "@angular/core";
import { MetaDefinition } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, firstValueFrom } from "rxjs";
import { TipoTarjeta } from "@src/app/models/enums/tipoTarjeta.enum";
import { ConfigService } from "src/app/servicios/config/config.service";
import { GfkService } from "src/app/servicios/gfk/gfk.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { TitleWrapperService } from "src/app/servicios/title-wrapper/title-wrapper.service";
import { PostListadoComponent } from "../post-listado/post-listado.component";
import { TarjetaInfoAutorComponent } from "../tarjeta-redes/tarjeta-info-autor.component";
import { BlogService } from "@src/app/servicios/blog/blog.service";
import { DetalleBlog } from "@src/app/models/interfaces/blog.interface";
import { CanonicalService } from "@src/app/servicios/canonical/canonical.service";

const URLBLOG_KEY = makeStateKey<any>("listado-posts-url-blog");
const POST_KEY = makeStateKey<any>("listado-posts-blog");
@Component({
  selector: "app-vista-post",
  templateUrl: "./vista-post.component.html",
  styleUrls: ["./vista-post.component.css"],
  imports: [TarjetaInfoAutorComponent, PostListadoComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BlogService],
})
export class VistaPostComponent implements OnInit, OnDestroy {
  constantesGlobales: any;
  posts = [];
  urlBlog: string = "";
  blog: any = [];
  tipoTarjeta = TipoTarjeta.Blog;
  rSub: Subscription;

  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly blogService: BlogService,
    private readonly titleWrapperService: TitleWrapperService,
    private readonly platformService: PlatformService,
    private readonly gfkService: GfkService,
    private readonly configService: ConfigService,
    private readonly transferState: TransferState,
    private readonly injector: Injector,
    private readonly cdr: ChangeDetectorRef,
    private readonly canonicalService: CanonicalService,
  ) {
    this.constantesGlobales = this.configService.getConstantesGlobales();
  }

  ngOnInit(): void {
    this.getBlogPosts();
    if (this.constantesGlobales.gfk === true) {
      if (this.platformService.isBrowser()) {
        this.initGfk();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.rSub) this.rSub.unsubscribe();
    this.transferState.remove(POST_KEY);
    this.transferState.remove(URLBLOG_KEY);
  }

  getBlogPosts() {
    if (!this.platformService.isBrowser()) {
      this.fetchPostsAndStoreKey();
    } else {
      if (this.transferState.hasKey(POST_KEY) && this.transferState.hasKey(URLBLOG_KEY)) {
        const detalleBlog = this.transferState.get<DetalleBlog>(POST_KEY, null);
        this.setResult(detalleBlog);
        this.urlBlog = this.transferState.get<string>(URLBLOG_KEY, null);
        this.transferState.remove(POST_KEY);
      } else {
        this.fetchPostsAndStoreKey();
      }
    }
  }

  /**
   * Obtiene las publicaciones del blog basadas en el parámetro `slug` de la URL,
   * almacena los datos obtenidos en el estado de transferencia y maneja errores.
   *
   * @remarks
   * Este método utiliza `ActivatedRoute` para suscribirse a los parámetros de la ruta
   * y obtener el `slug` del blog. Luego, llama al servicio `blogService` para obtener
   * las publicaciones relacionadas con el `slug`. Si los datos son recuperados con éxito,
   * se almacenan en el estado de transferencia y se actualiza la vista. En caso de error,
   * redirige al usuario a una página 404.
   *
   * @returns {void} Este método no retorna ningún valor.
   *
   * @throws {Error} Lanza un error si ocurre un problema al obtener las publicaciones del blog.
   */
  fetchPostsAndStoreKey(): void {
    this.rSub = this.activatedRoute.params.subscribe(async (parms: any) => {
      this.urlBlog = parms.slug;
      const postBlogs = this.blogService.getPostsBlog(50, parms.slug, "slug");
      if (postBlogs) {
        try {
          const DetalleBlog: DetalleBlog = await firstValueFrom(postBlogs);
          this.setResult(DetalleBlog);
          this.transferState.set<any>(POST_KEY, DetalleBlog);
          this.cdr.detectChanges();
        } catch (error) {
          const router = this.injector.get(Router);
          router.navigate(["/404"]);
        }
      }
      this.transferState.set<any>(URLBLOG_KEY, parms.slug);
    });
  }

  setResult(detalleBlog: DetalleBlog): void {
    if (detalleBlog.blog.blogInfo) {
      this.blog = detalleBlog.blog.blogInfo;
      if (this.blog) {
        const titulo = this.blog?.nombreSubseccion + " - " + "Por " + this.blog.nombre;
        this.titleWrapperService.setTitle(titulo);
      }
      this.posts = detalleBlog.video;
      this.updateMetaTags();
    } else {
      this.router.navigateByUrl("/404");
    }
  }

  /**
   * Actualiza las metaetiquetas de la página con información relevante del post actual.
   * Genera un array de metaetiquetas que incluye datos como descripción, título, autor,
   * editor, y otros metadatos relacionados con el contenido del post.
   *
   * Las metaetiquetas generadas incluyen tanto etiquetas estándar como etiquetas específicas
   * para Open Graph (og) y Dublin Core (DC).
   *
   * @private
   */
  updateMetaTags(): void {
    const metaTagsArray: MetaDefinition[] = [
      { property: "og:title", content: this.blog?.nombreSubseccion + " - " + "Por " + this.blog?.nombre },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${this.constantesGlobales.siteDomain}/assets/images/${this.constantesGlobales.imgShare}` },
      { property: "og:url", content: this.constantesGlobales.siteDomain },
      { property: "og:description", content: this.constantesGlobales.metaTag },
      { property: "article:author", content: this.constantesGlobales.siteName },
      { property: "article:publisher", content: this.constantesGlobales.siteName },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
      { name: "DC.title", content: this.constantesGlobales.siteTitle },
      { name: "DC.creator", content: this.constantesGlobales.siteTitle },
      { name: "DC.publisher", content: this.constantesGlobales.siteTitle },
      { name: "DC.description", content: this.constantesGlobales.metaTag },
      { name: "DC.subject", content: this.constantesGlobales.metaTag },
      { name: "DC.date", content: new Date() },
    ];

    this.canonicalService.updateCanonicalUrls(metaTagsArray);
  }

  private initGfk(): void {
    if (this.constantesGlobales.gfk) {
      const customParams = { c1: this.stripProtocolAndSubdomain(this.constantesGlobales.siteDomain), c2: "blogs" };
      this.gfkService.impression(customParams);
    }
  }

  stripProtocolAndSubdomain(url: string): string {
    const strippedUrl = url.replace(/^(https?:\/\/)?(www\.)?/, "");
    return strippedUrl;
  }
}
